import { Controller } from "@hotwired/stimulus"
import { animateWindowScroll, catalogScrollTop, websiteHeaderOffset } from "../../lib/window_scroll"

const SCROLL_POSITION_KEY = "website.saleCatalog.scrollY"
const SCROLL_PATH_KEY = "website.saleCatalog.pathname"
const AUTO_SCROLL_DURATION = 500
const SIDEBAR_BOTTOM_GAP = 16
const TABLET_BREAKPOINT = 768

export default class extends Controller {
  connect() {
    this.element.dataset.salePageReady = "true"
    this.visitDirection = document.documentElement.dataset.turboVisitDirection
    this.saveScrollHandler = () => this.saveScroll()
    window.addEventListener("pagehide", this.saveScrollHandler)
    this.sidebarResizeHandler = () => this.updateSidebarLayout()
    this.sidebarScrollHandler = () => this.scheduleSidebarPositionUpdate()
    window.addEventListener("resize", this.sidebarResizeHandler)
    window.addEventListener("scroll", this.sidebarScrollHandler, { passive: true })
    this.observeSidebar()

    if (this.visitDirection) return

    if (document.readyState === "complete") {
      this.restoreOrScroll({ restoreStoredPosition: this.isHistoryNavigation() })
    } else {
      this.loadHandler = () => this.restoreOrScroll({ restoreStoredPosition: this.isHistoryNavigation() })
      window.addEventListener("load", this.loadHandler, { once: true })
    }
  }

  disconnect() {
    window.removeEventListener("pagehide", this.saveScrollHandler)
    window.removeEventListener("resize", this.sidebarResizeHandler)
    window.removeEventListener("scroll", this.sidebarScrollHandler)
    this.stopObservingSidebar()
    if (this.loadHandler) window.removeEventListener("load", this.loadHandler)
    if (this.restoreFrame) cancelAnimationFrame(this.restoreFrame)
    if (this.autoScrollFrame) cancelAnimationFrame(this.autoScrollFrame)
    if (this.sidebarPositionFrame) cancelAnimationFrame(this.sidebarPositionFrame)
    this.cancelAutoScroll?.()
    delete this.element.dataset.salePageReady
    delete this.element.dataset.salePageScrollRestored
    delete this.element.dataset.salePageAutoScrollDuration
    delete this.element.dataset.salePageAutoScrollComplete
  }

  restoreOrScrollAfterTurboVisit() {
    if (!this.visitDirection) return

    const restoreStoredPosition = this.visitDirection === "back"
    this.visitDirection = null
    this.restoreOrScroll({ restoreStoredPosition })
  }

  restoreOrScroll({ restoreStoredPosition }) {
    const storedPath = sessionStorage.getItem(SCROLL_PATH_KEY)
    const storedPosition = Number(sessionStorage.getItem(SCROLL_POSITION_KEY))
    sessionStorage.removeItem(SCROLL_POSITION_KEY)
    sessionStorage.removeItem(SCROLL_PATH_KEY)

    if (restoreStoredPosition && storedPath === window.location.pathname && storedPosition > 0) {
      this.restoreFrame = requestAnimationFrame(() => {
        this.restoreFrame = null
        window.scrollTo(0, storedPosition)
        this.element.dataset.salePageScrollRestored = String(storedPosition)
      })
      return
    }

    this.scrollPastCover()
  }

  scrollPastCover() {
    const cover = document.getElementById("image-home")
    const sidebar = document.getElementById("sidebar")
    if (!cover || !sidebar || window.innerWidth < 768 || window.scrollY !== 0) return

    this.autoScrollFrame = requestAnimationFrame(() => {
      this.autoScrollFrame = null
      if (window.scrollY !== 0) return
      this.element.dataset.salePageAutoScrollDuration = String(AUTO_SCROLL_DURATION)
      this.cancelAutoScroll = animateWindowScroll(catalogScrollTop(sidebar, this.sidebar), {
        duration: AUTO_SCROLL_DURATION,
        onComplete: () => {
          this.cancelAutoScroll = null
          this.element.dataset.salePageAutoScrollComplete = "true"
        }
      })
    })
  }

  catalogLoaded(event) {
    if (event.target.id !== "sale-catalog") return

    this.observeSidebar()
  }

  rememberCatalogPosition() {
    this.saveScroll()
  }

  observeSidebar() {
    this.stopObservingSidebar()
    this.sidebar = this.element.querySelector("#sidebar #fixed")
    if (!this.sidebar) return

    this.sidebarObserver = new ResizeObserver(this.sidebarResizeHandler)
    this.sidebarObserver.observe(this.sidebar)
    this.updateSidebarLayout()
  }

  stopObservingSidebar() {
    this.sidebarObserver?.disconnect()
    if (this.sidebarPositionFrame) cancelAnimationFrame(this.sidebarPositionFrame)
    this.sidebarPositionFrame = null
    this.resetSidebarPosition()
    this.sidebarObserver = null
    this.sidebar = null
  }

  updateSidebarLayout() {
    if (!this.sidebar) return

    const availableHeight = window.innerHeight - websiteHeaderOffset() - SIDEBAR_BOTTOM_GAP
    const scrollTracked = window.innerWidth >= TABLET_BREAKPOINT && this.sidebar.scrollHeight > availableHeight
    const wasScrollTracked = this.sidebar.classList.contains("is-scroll-tracked")

    this.sidebar.classList.toggle("is-scroll-tracked", scrollTracked)
    this.sidebar.dataset.sidebarMode = scrollTracked ? "scroll-tracked" : "sticky"

    if (scrollTracked) {
      if (!wasScrollTracked) this.clearTrackedSidebarPosition()
      this.updateSidebarPosition()
    } else {
      this.clearTrackedSidebarPosition()
    }
  }

  scheduleSidebarPositionUpdate() {
    if (!this.sidebar?.classList.contains("is-scroll-tracked") || this.sidebarPositionFrame) return

    this.sidebarPositionFrame = requestAnimationFrame(() => {
      this.sidebarPositionFrame = null
      this.updateSidebarPosition()
    })
  }

  updateSidebarPosition() {
    if (!this.sidebar?.classList.contains("is-scroll-tracked")) return

    const container = this.sidebar.parentElement
    const sidebarStyle = getComputedStyle(this.sidebar)
    const marginTop = Number.parseFloat(sidebarStyle.marginTop) || 0
    const marginBottom = Number.parseFloat(sidebarStyle.marginBottom) || 0
    const sidebarHeight = this.sidebar.getBoundingClientRect().height
    const containerRect = container.getBoundingClientRect()
    const naturalDocumentTop = containerRect.top + window.scrollY + marginTop
    const maxOffset = Math.max(0, containerRect.height - sidebarHeight - marginTop - marginBottom)
    const maximumDocumentTop = naturalDocumentTop + maxOffset
    const viewportBottomDocumentTop = window.scrollY + window.innerHeight - SIDEBAR_BOTTOM_GAP - sidebarHeight
    const viewportTopDocumentTop = window.scrollY + websiteHeaderOffset()
    const currentDocumentTop = this.sidebarDocumentTop ?? naturalDocumentTop
    const viewportConstrainedTop = Math.min(
      viewportTopDocumentTop,
      Math.max(viewportBottomDocumentTop, currentDocumentTop)
    )
    const documentTop = Math.min(maximumDocumentTop, Math.max(naturalDocumentTop, viewportConstrainedTop))
    const offset = documentTop - naturalDocumentTop

    this.sidebarDocumentTop = documentTop
    this.sidebar.dataset.sidebarOffset = String(Math.round(offset))

    if (documentTop > naturalDocumentTop && documentTop < maximumDocumentTop) {
      if (Math.abs(documentTop - viewportBottomDocumentTop) < 0.5) {
        this.pinSidebar(containerRect, window.innerHeight - SIDEBAR_BOTTOM_GAP - sidebarHeight, marginTop, "bottom")
        return
      }

      if (Math.abs(documentTop - viewportTopDocumentTop) < 0.5) {
        this.pinSidebar(containerRect, websiteHeaderOffset(), marginTop, "top")
        return
      }
    }

    this.sidebar.style.removeProperty("position")
    this.sidebar.style.removeProperty("top")
    this.sidebar.style.removeProperty("left")
    this.sidebar.style.transform = `translateY(${offset}px)`
    this.sidebar.dataset.sidebarState = "flow"
  }

  pinSidebar(containerRect, viewportTop, marginTop, edge) {
    this.sidebar.style.position = "fixed"
    this.sidebar.style.top = `${viewportTop - marginTop}px`
    this.sidebar.style.left = `${containerRect.left}px`
    this.sidebar.style.removeProperty("transform")
    this.sidebar.dataset.sidebarState = `pinned-${edge}`
  }

  clearTrackedSidebarPosition() {
    if (!this.sidebar) return

    this.sidebar.style.removeProperty("position")
    this.sidebar.style.removeProperty("top")
    this.sidebar.style.removeProperty("left")
    this.sidebar.style.removeProperty("transform")
    delete this.sidebar.dataset.sidebarOffset
    delete this.sidebar.dataset.sidebarState
    this.sidebarDocumentTop = null
  }

  resetSidebarPosition() {
    if (!this.sidebar) return

    this.sidebar.classList.remove("is-scroll-tracked")
    this.clearTrackedSidebarPosition()
    delete this.sidebar.dataset.sidebarMode
  }

  isHistoryNavigation() {
    const navigation = performance.getEntriesByType?.("navigation")[0]
    return navigation?.type === "reload" || navigation?.type === "back_forward"
  }

  saveScroll() {
    sessionStorage.setItem(SCROLL_POSITION_KEY, String(window.scrollY))
    sessionStorage.setItem(SCROLL_PATH_KEY, window.location.pathname)
  }
}
